import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { clearPlyxAttribution } from "@/lib/plyxAttribution";

import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(sessionId ? "polling" : "failed");
  const [txn, setTxn] = useState(null);

  useEffect(() => {
    if (!sessionId) return undefined;
    let attempts = 0;
    const max = 8;
    let cancelled = false;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setTxn(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          // Clear the 24h Plyx attribution session — it's been spent on this sale.
          clearPlyxAttribution();
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attempts >= max) { setStatus("failed"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        if (attempts >= max) setStatus("failed");
        else setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="container-narrow py-24 max-w-xl text-center">
      {status === "polling" && (
        <>
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-[#C05A3C] mb-4"/>
          <h1 className="font-display text-3xl">Confirming your payment…</h1>
          <p className="text-muted-foreground mt-2">Hang tight, this only takes a moment.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-14 h-14 mx-auto text-[#4A5D23] mb-4"/>
          <h1 className="font-display text-4xl">{txn?.payment_type === "pro_subscription" ? "Welcome to Pro 👑" : txn?.payment_type === "verified_badge" ? "You're verified ✓" : "Payment received 🎉"}</h1>
          <p className="text-muted-foreground mt-3">
            {txn?.payment_type === "pro_subscription"
              ? "Your Pro benefits are active. Lower fees, verified badge and priority placement — all unlocked."
              : txn?.payment_type === "verified_badge"
              ? "Your verified badge is now showing on every listing. Trust unlocked."
              : "The seller has been notified. You can message them anytime from your dashboard."}
          </p>
          {txn && (
            <div className="mt-4 text-sm text-muted-foreground">
              {(({ gbp: "£", usd: "$", eur: "€", cad: "C$" })[txn.currency?.toLowerCase()] || (txn.currency?.toUpperCase() + " "))}{txn.amount}
              {txn.is_founder_price && <span className="ml-2 text-[#D9923B] font-semibold">· Founder price locked for life</span>}
            </div>
          )}
          {txn?.plyx_attribution_creator?.handle && (
            <div
              className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-gradient-to-r from-[#FF2D7A]/10 via-[#FFB42D]/10 to-[#00E0C7]/10 border border-[#FF2D7A]/25"
              data-testid="plyx-attribution-thanks"
            >
              <span className="text-base leading-none">💜</span>
              <span className="text-[#1A2024]">
                <Link to={`/plyx/u/${txn.plyx_attribution_creator.handle}`} className="font-semibold hover:underline">
                  @{txn.plyx_attribution_creator.handle}
                </Link>{" "}
                earns a creator share on this purchase
              </span>
            </div>
          )}
          <div className="mt-8 flex justify-center gap-3">
            <Button asChild className="bg-[#C05A3C] hover:bg-[#D46B4E] rounded-md"><Link to="/dashboard">Go to dashboard</Link></Button>
            <Button asChild variant="outline"><Link to="/browse">Keep shopping</Link></Button>
          </div>
        </>
      )}
      {(status === "failed" || status === "expired") && (
        <>
          <XCircle className="w-14 h-14 mx-auto text-[#B84A28] mb-4"/>
          <h1 className="font-display text-3xl">Payment {status === "expired" ? "expired" : "incomplete"}</h1>
          <p className="text-muted-foreground mt-2">No charge was completed. You can try again from the listing.</p>
          <Button asChild className="mt-6 rounded-md"><Link to="/browse">Back to listings</Link></Button>
        </>
      )}
    </div>
  );
};

export default PaymentSuccess;
